import {
    useState, useEffect, useCallback, useRef, useMemo,
    type ReactNode, type MouseEvent as MouseEventReact, type TouchEvent as TouchEventReact,
} from 'react';
import { useAbrirNovo } from '../hooks/useAbrirNovo';
import aviso, { textoDoErro } from '../lib/aviso';
import { useCelular } from '../hooks/useCelular';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import {
    LuPlus, LuSearch, LuKanban, LuList, LuBot, LuUserPlus, LuChevronsLeft, LuChevronsRight,
    LuChevronLeft, LuChevronRight, LuChevronUp, LuChevronDown, LuChevronsUpDown, LuEye, LuTrash2, LuFilterX, LuPencil, LuMegaphone, LuArrowUpDown, LuMessageCircle, LuCalendar,
    LuWebhook, LuDownload, LuSettings,
} from 'react-icons/lu';
import { exportarListaCsv } from '../services/exportarCsv';
import { textoOrigemMarketing } from '../utils/origemMarketing';
import { nomeEtapa, NOME_ETAPA, palavraDoCanal, gruposDoFunil, FUNIL_CARREGADO_EVENT, type GrupoEtapa } from '../data/etapas';
import { PARADOS, diasDoCorte } from '../data/semInteracao';
import { FaWhatsapp, FaInstagram, FaFacebookMessenger } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import Select from '../components/Select';
import {
    ListRow, RowAction, RowActions, PerPage, RowConfirm,
    FiltrosBotao, FiltrosResumo, FiltrosPainel, FiltroColuna, FiltroCampo, FiltroChips, FiltroMulti,
} from '../components/ListKit';
import '../components/ListKit.css';
import './PipelineBoard.css';
import { apiFetch } from '../services/api';
import { OpportunityService, type Opportunity, type DetalheConversa, type OrigemMarketing } from '../services/OpportunityService';
import { ChatService } from '../services/ChatService';
import { TipoContatoService, type TipoContato } from '../services/TipoContatoService';
import { ContactService } from '../services/ContactService';
import PipelineSkeleton from '../components/PipelineSkeleton';
import MenuContexto from '../components/MenuContexto';
import { itensDoNegocio, type AcoesNegocio } from '../components/negocioMenu';
import SummaryModal from '../components/SummaryModal';
import TransferModal from '../components/TransferModal';
import CriarTarefaDialog from '../components/CriarTarefaDialog';
import { TipoTarefaService, type TipoTarefa } from '../services/TarefaService';
import { type InternalUser } from '../data/mockData';
import WinDealProductModal from '../components/WinDealProductModal';
import LossReasonModal from '../components/LossReasonModal';
import CreateOpportunityPanel from '../components/CreateOpportunityPanel';
import RdStationSimbolo from '../components/apps/RdStationSimbolo';
import Dialog from '../components/Dialog';
import TourNovidade from '../components/TourNovidade';
import { TOUR_CONFIGURAR_FUNIL } from '../config/novidades';
import { PERMISSOES, temPermissao, ehAdministradorDaEmpresa, temFuncionalidade } from '../config/permissions';
import { ACAO_ASSISTENTE_EVENT } from '../services/AssistenteService';

const apiUrl = import.meta.env.VITE_API_URL;

/* ─── Modelo ──────────────────────────────────────────────────── */

interface Deal {
    id: number;
    title: string;
    value: string;
    tag: 'hot' | 'warm' | 'cold' | 'new';
    owner: string;
    date: string;
    rawDate: Date;
    contactId: number;
    statusId: number;
    tipoLeadId?: number;
    /** Tipo do contato (lead, fornecedor…); nulo = sem tipo. */
    tipoContatoId?: number | null;
    rawValue?: number;
    phone?: string;
    details?: DetalheConversa[];
    campanha?: boolean;
    idCampanha?: number | null;
    nomeCampanha?: string;
    ownerId?: number;
    idOrigemOportunidade?: number;
    descricaoOrigemOportunidade?: string;
    /* De ONDE o lead veio (busca paga, utm, formulário). O de cima é o canal, onde se conversa. */
    origemMarketing?: OrigemMarketing | null;
    organizacaoId?: number | null;
    organizacao?: string;
    /**
     * Há quantos dias o negócio está parado: a última mensagem da conversa (qualquer direção) ou,
     * quando não há mensagem nenhuma, a criação. É o que o filtro "Sem interação" lê.
     */
    diasParado: number;
}

interface Column {
    id: string;
    title: string;
    statusId: number;
    deals: Deal[];
}

/**
 * As sete etapas da API, uma coluna cada. É o modelo — o celular ainda recebe
 * este formato — e o quadro só agrupa na hora de desenhar (ver GRUPOS).
 */
const INITIAL_COLUMNS: Column[] = [
    { id: 'ai_service', title: NOME_ETAPA[1], statusId: 1, deals: [] },
    { id: 'ai_service_finished', title: NOME_ETAPA[8], statusId: 8, deals: [] },
    { id: 'intro', title: NOME_ETAPA[2], statusId: 2, deals: [] },
    { id: 'proposal', title: NOME_ETAPA[4], statusId: 4, deals: [] },
    { id: 'qualified', title: NOME_ETAPA[5], statusId: 5, deals: [] },
    { id: 'closed', title: NOME_ETAPA[3], statusId: 3, deals: [] },
    { id: 'lost', title: 'Venda Perdida', statusId: 6, deals: [] },
];


/* A coluna do quadro é a etapa do funil: o formato continua o mesmo, mas quem o
   monta agora é `data/etapas` a partir do que a empresa cadastrou. */
type Grupo = GrupoEtapa;

const grupoDoStatus = (statusId: number) => gruposDoFunil().find(g => g.statusIds.includes(statusId));

const TEMPERATURA: Record<number, { rotulo: string; classe: string }> = {
    1: { rotulo: 'Quente', classe: 'is-hot' },
    2: { rotulo: 'Morno', classe: 'is-warm' },
    3: { rotulo: 'Frio', classe: 'is-cold' },
};

const PERIODOS = ['7 dias', '15 dias', '30 dias', 'Personalizado'] as const;

/* "Sem interação" (há quantos dias o negócio está parado) é a mesma régua do Painel Operacional
   desde 14/10/2026 — ela mora em `data/semInteracao` e ganhou os cortes curtos (24h, 2 e 3 dias).
   Antes começava em 7, e "quem está parado há 2 dias" não tinha como ser perguntado. */

const DIA_MS = 86400000;
/** Dias cheios entre a data e agora. Hoje é 0. */
const diasDesde = (quando: Date) => Math.max(0, Math.floor((Date.now() - quando.getTime()) / DIA_MS));

/** Conjuntos fixos do painel de filtros: vão como opções à vista, não como lista. */
const TEMPERATURAS = Object.values(TEMPERATURA).map(t => ({ valor: t.rotulo }));
/* Veio de campanha ou não. Os valores ficam como eram ('Orgânica'/'Campanha'): é o
   que a sessão salva; só o rótulo mudou quando "Origem" passou a ser o canal (05/10). */
const ORIGENS = [{ valor: 'Orgânica', rotulo: 'Sem campanha' }, { valor: 'Campanha', rotulo: 'De campanha' }];

/** O chip de quem não passou por porta rastreada: filtrar por ele é ver o que não tem origem. */
const SEM_ORIGEM = 'Sem origem';

/** A ordem dos canais no filtro Origem — a mesma palavra da coluna (ver `origem`). */
const ORDEM_CANAIS = ['WhatsApp', 'Instagram', 'Messenger', 'Lead Ads', 'RD Station', 'Webhook', 'Manual', 'Conversa'];

/**
 * Ordem = `coluna:direcao`. No quadro ela vem da pílula (três atalhos); na
 * lista, das setas do cabeçalho — qualquer coluna, nos dois sentidos (pedido
 * dele, 2026-09-13). Os dois escrevem o mesmo estado.
 */
type ColunaOrdem = 'titulo' | 'organizacao' | 'etapa' | 'valor' | 'temperatura' | 'responsavel' | 'origem' | 'criada';
type Direcao = 'asc' | 'desc';
const ORDENS = [
    { value: 'criada:desc', label: 'Mais recentes' },
    { value: 'criada:asc', label: 'Mais antigas' },
    { value: 'valor:desc', label: 'Maior valor' },
];
/** Valores gravados antes da ordenação por coluna. */
const migrarOrdem = (v?: string): string => {
    if (v === 'recentes' || !v) return 'criada:desc';
    if (v === 'antigas') return 'criada:asc';
    if (v === 'valor') return 'valor:desc';
    return v;
};
const lerOrdem = (v: string): { col: ColunaOrdem; dir: Direcao } => {
    const [col, dir] = v.split(':');
    return { col: (col || 'criada') as ColunaOrdem, dir: dir === 'asc' ? 'asc' : 'desc' };
};
/** Primeiro clique numa coluna: número e data descem, texto sobe. */
const DIRECAO_PADRAO: Record<ColunaOrdem, Direcao> = {
    titulo: 'asc', organizacao: 'asc', etapa: 'asc', valor: 'desc', temperatura: 'asc', responsavel: 'asc', origem: 'asc', criada: 'desc',
};
const POSICAO_ETAPA = (statusId: number) => { const grupos = gruposDoFunil(); const i = grupos.findIndex(g => g.statusIds.includes(statusId)); return i < 0 ? grupos.length : i; };
const texto = (a: string | undefined, b: string | undefined) => (a ?? '').localeCompare(b ?? '', 'pt-BR', { sensitivity: 'base' });
/** O que conta como "sem valor" na coluna: vai para o fim nos dois sentidos, fora da inversão. */
const VAZIO: Partial<Record<ColunaOrdem, (d: Deal) => boolean>> = {
    organizacao: d => !d.organizacao,
    valor: d => !d.rawValue,
    temperatura: d => !d.tipoLeadId,
    responsavel: d => !d.ownerId && (!d.owner || d.owner === 'IA' || d.owner === 'Sistema'),
};
const COMPARADORES: Record<ColunaOrdem, (a: Deal, b: Deal) => number> = {
    titulo: (a, b) => texto(a.title, b.title),
    organizacao: (a, b) => texto(a.organizacao, b.organizacao),
    etapa: (a, b) => POSICAO_ETAPA(a.statusId) - POSICAO_ETAPA(b.statusId),
    valor: (a, b) => (a.rawValue ?? 0) - (b.rawValue ?? 0),
    temperatura: (a, b) => (a.tipoLeadId || 4) - (b.tipoLeadId || 4),
    responsavel: (a, b) => texto(a.owner, b.owner),
    origem: (a, b) => texto(origem(a.idOrigemOportunidade).palavra, origem(b.idOrigemOportunidade).palavra),
    criada: (a, b) => a.rawDate.getTime() - b.rawDate.getTime(),
};

const POR_PAGINA = [25, 50, 100];

/** Quanto o dedo precisa ficar parado no cartao para abrir o menu (o toque curto continua abrindo). */
const TOQUE_LONGO_MS = 500;

// Origem virou só o glifo (60px) para a Organização caber como coluna.
const COLUNAS_LISTA = '1fr 1.2fr 1.05fr .8fr .9fr 1.4fr 88px .6fr 78px';
/* Quem pode excluir tem tres icones na coluna de acoes (ver, tirar do funil, excluir) e a coluna
   cresce um botao: 26px do icone + 2px de respiro. Quem nao pode fica com os 78px de sempre —
   largura de tres com dois icones abriria vao a esquerda. */
const COLUNAS_LISTA_ADMIN = '1fr 1.2fr 1.05fr .8fr .9fr 1.4fr 88px .6fr 106px';

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const brl = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const dataCurta = (d: Date) => `${String(d.getDate()).padStart(2, '0')} ${MESES[d.getMonth()]}`;

const iniciais = (nome: string) => nome.trim().split(/\s+/).filter(Boolean).slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '').join('') || '?';

/** Um telefone no lugar do nome é identidade provisória — lê mais baixo. */
const pareceTelefone = (s: string) => /^\+?[\d\s()-]{8,}$/.test(s);

const lerLocal = (chave: string, padrao: string) => {
    try { return localStorage.getItem(chave) ?? padrao; } catch { return padrao; }
};
const gravarLocal = (chave: string, valor: string) => {
    try { localStorage.setItem(chave, valor); } catch { /* preferência de leitura, não dado */ }
};

/** Filtros da sessão: sobrevivem à ida e volta da oportunidade, morrem com a aba. */
type FiltrosSalvos = {
    busca: string; responsavel: string[]; temperatura: string[]; origemFiltro: string[]; canal: string[]; origemLead: string[];
    organizacaoFiltro: string[]; campanha: string[]; periodo: string | null; dataDe: string; dataAte: string;
    etapa: string[]; semInteracao: string | null; ordem: string; porPagina: number; pagina: number;
    tipoContato: string[];
};
const FILTROS_CHAVE = 'pl:filtros';
const lerFiltros = (): Partial<FiltrosSalvos> => {
    try { return JSON.parse(sessionStorage.getItem(FILTROS_CHAVE) ?? '{}') ?? {}; } catch { return {}; }
};
/** Até 26/09 cada filtro guardava UM valor (string | null); a sessão aberta pode trazer o formato velho. */
const comoLista = (v: unknown): string[] => (Array.isArray(v) ? v.filter(x => typeof x === 'string') : typeof v === 'string' && v ? [v] : []);
const gravarFiltros = (f: FiltrosSalvos) => {
    try { sessionStorage.setItem(FILTROS_CHAVE, JSON.stringify(f)); } catch { /* estado de trabalho, não dado */ }
};

/**
 * Glifo do canal, do OriginBadge (PlataformaEnum): 1 e 2 WhatsApp, 3 Instagram,
 * 4 Messenger, 5 Lead Ads, 6 Manual, 7 RD Station. Marca continua sendo glifo de marca.
 */
const origem = (id?: number): { glifo: ReactNode; palavra: string } => {
    /* A palavra mora em `data/etapas` desde 14/10/2026, porque o Painel Operacional filtra pelo
       mesmo canal e duas tabelas se separam na primeira plataforma nova. O glifo fica aqui: é
       marca, e só esta tela desenha. */
    const palavra = palavraDoCanal(id);
    switch (id) {
        case 1: case 2: return { glifo: <FaWhatsapp size={12} className="pl-wa" />, palavra };
        case 3: return { glifo: <FaInstagram size={12} />, palavra };
        case 4: return { glifo: <FaFacebookMessenger size={12} />, palavra };
        case 5: return { glifo: <LuMegaphone size={12} />, palavra };
        case 6: return { glifo: <LuPencil size={12} />, palavra };
        case 7: return { glifo: <RdStationSimbolo size={12} />, palavra };
        case 8: return { glifo: <LuWebhook size={12} />, palavra };
        default: return { glifo: <LuMessageCircle size={12} />, palavra };
    }
};

/* ─── Tela ────────────────────────────────────────────────────── */

const PipelinePage = () => {
    const [columns, setColumns] = useState<Column[]>(INITIAL_COLUMNS);
    const [isLoading, setIsLoading] = useState(true);
    // O detalhe é página (/pipeline/deal/:id) desde a fase 2 — abrir é navegar.
    const navigate = useNavigate();
    const abrir = (deal: Deal) => navigate(`/pipeline/deal/${deal.id}`);
    const [currentUser, setCurrentUser] = useState<{ id: number; nome: string } | null>(null);

    // Vista e Perdido aberta são preferências de leitura: ficam no navegador.
    const [view, setView] = useState<'quadro' | 'lista'>(() => (lerLocal('pl:view', 'quadro') === 'lista' ? 'lista' : 'quadro'));
    const [perdidoAberta, setPerdidoAberta] = useState(() => lerLocal('pl:lost-open', '0') === '1');
    const usaTipoContato = temFuncionalidade('tipo_contato');
    /* A lista de tipos muda pouco e serve só ao filtro: uma chamada por abertura da tela. */
    useEffect(() => {
        if (!usaTipoContato) return;
        let vivo = true;
        TipoContatoService.getAll()
            .then(r => { if (vivo && r.sucesso) setTiposContato(r.dados || []); })
            .catch(() => { /* sem a lista o filtro não aparece; a Pipeline abre igual */ });
        return () => { vivo = false; };
    }, [usaTipoContato]);

    useEffect(() => gravarLocal('pl:view', view), [view]);
    useEffect(() => gravarLocal('pl:lost-open', perdidoAberta ? '1' : '0'), [perdidoAberta]);

    // Filtros — guardados na sessão da aba: abrir uma oportunidade e voltar
    // encontra o mesmo recorte (ele, 2026-09-13: "aquele filtro se perdeu").
    const [salvo] = useState(lerFiltros);
    const [busca, setBusca] = useState(salvo.busca ?? '');
    // Mais de um valor por filtro desde 26/09 (dois vendedores, duas temperaturas).
    const [responsavel, setResponsavel] = useState<string[]>(() => comoLista(salvo.responsavel));
    const [temperatura, setTemperatura] = useState<string[]>(() => comoLista(salvo.temperatura));
    const [origemFiltro, setOrigemFiltro] = useState<string[]>(() => comoLista(salvo.origemFiltro));
    /* De onde o lead veio (busca paga, utm, formulário), quando a porta de entrada soube dizer.
       Separado do canal de propósito: canal é onde se conversa, isto é de onde a pessoa veio. */
    const [origemLead, setOrigemLead] = useState<string[]>(() => comoLista(salvo.origemLead));
    // O canal (WhatsApp, Lead Ads…) — a mesma "Origem" da coluna. Até 05/10 o filtro
    // Origem só separava campanha de não campanha, e Lead Ads não tinha como ser filtrado.
    const [canal, setCanal] = useState<string[]>(() => comoLista(salvo.canal));
    const [organizacaoFiltro, setOrganizacaoFiltro] = useState<string[]>(() => comoLista(salvo.organizacaoFiltro));
    const [campanha, setCampanha] = useState<string[]>(() => comoLista(salvo.campanha));
    const [periodo, setPeriodo] = useState<string | null>(salvo.periodo ?? null);
    const [dataDe, setDataDe] = useState(salvo.dataDe ?? '');
    const [dataAte, setDataAte] = useState(salvo.dataAte ?? '');
    const [etapa, setEtapa] = useState<string[]>(() => comoLista(salvo.etapa));
    /* Tipo de contato: a lista é da empresa e vem uma vez; o filtro casa pelo ID, não pelo nome,
       porque o nome é editável pela própria empresa. */
    const [tipoContato, setTipoContato] = useState<string[]>(() => comoLista(salvo.tipoContato));
    const [tiposContato, setTiposContato] = useState<TipoContato[]>([]);
    /* "Sem interação": escolha única, como o período — "parado há mais de 7 E mais de 30 dias" não
       quer dizer nada; o corte maior já contém o menor. */
    const [semInteracao, setSemInteracao] = useState<string | null>(salvo.semInteracao ?? null);
    const [filtrosAbertos, setFiltrosAbertos] = useState(false);
    const filtrosAncora = useRef<HTMLButtonElement>(null);
    const [ordem, setOrdem] = useState(() => migrarOrdem(salvo.ordem));
    const ordemAtual = lerOrdem(ordem);
    const ordenarPor = (col: ColunaOrdem) => {
        const dir: Direcao = ordemAtual.col === col ? (ordemAtual.dir === 'asc' ? 'desc' : 'asc') : DIRECAO_PADRAO[col];
        setOrdem(`${col}:${dir}`);
    };

    // Lista
    const [porPagina, setPorPagina] = useState(salvo.porPagina ?? 25);
    const [pagina, setPagina] = useState(salvo.pagina ?? 1);
    useEffect(() => {
        gravarFiltros({ busca, responsavel, temperatura, origemFiltro, canal, origemLead, organizacaoFiltro, campanha, periodo, dataDe, dataAte, etapa, semInteracao, ordem, porPagina, pagina, tipoContato });
    }, [busca, responsavel, temperatura, origemFiltro, canal, origemLead, organizacaoFiltro, campanha, periodo, dataDe, dataAte, etapa, semInteracao, ordem, porPagina, pagina]);
    const [confirmandoId, setConfirmandoId] = useState<number | null>(null);
    const [excluindo, setExcluindo] = useState(false);

    // Painel Nova oportunidade
    const [painelNovo, setPainelNovo] = useState<{ aberto: boolean; etapa?: number }>({ aberto: false });

    /* A paleta do Ctrl+K pediu "Nova oportunidade": abre o painel direto. */
    useAbrirNovo(() => setPainelNovo({ aberto: true }));

    // Interrupções do arrasto (as regras da seção 1 do desenho)
    const [pendente, setPendente] = useState<{ deal: Deal; origem: number; destino: number } | null>(null);
    const [isLossReasonModalOpen, setIsLossReasonModalOpen] = useState(false);
    const [isWinProductModalOpen, setIsWinProductModalOpen] = useState(false);
    const [pendingWonDeal, setPendingWonDeal] = useState<Deal | null>(null);

    const [assumingDealId, setAssumingDealId] = useState<number | null>(null);
    /** Oportunidade que o gestor pediu para tirar de outro vendedor — espera a confirmação. */
    const [tomando, setTomando] = useState<Deal | null>(null);
    /* Ver/assumir/exportar negócio de outro é a chave `conversas.todos`
       (o admin tem por padrão), não mais "é administrador". */
    const verTodos = temPermissao(PERMISSOES.conversasTodos);
    /* Excluir oportunidade e so do administrador da empresa. E pergunta de PAPEL
       (`tipoUsuarioCodigo`), nao chave do catalogo: nao e configuravel por perfil. A API recusa de
       qualquer jeito; aqui o botao some em vez de aparecer e dar erro. */
    const podeExcluir = ehAdministradorDaEmpresa();
    /* Configurar o funil (etapas da empresa) também é do administrador: mesma pergunta de papel. */
    const podeConfigurarFunil = ehAdministradorDaEmpresa();
    const colunasLista = podeExcluir ? COLUNAS_LISTA_ADMIN : COLUNAS_LISTA;

    const isDraggingRef = useRef(false);
    const boardRef = useRef<HTMLDivElement>(null);
    const scrollAnimationFrameRef = useRef<number | null>(null);

    /* O corte único do produto — ver `hooks/useCelular.ts`. */
    const isMobile = useCelular();

    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const response = await apiFetch(`${apiUrl}/api/Auth/me`);
                if (!response.ok) return;
                const result = await response.json();
                if (result.sucesso && result.dados) {
                    setCurrentUser({ id: result.dados.id || result.dados.userId || 0, nome: result.dados.userName || result.dados.nome });
                } else if (result.id) {
                    setCurrentUser({ id: Number(result.id) || 0, nome: result.userName || result.nome });
                }
            } catch (err) {
                console.error('Erro ao buscar usuário logado:', err);
            }
        };
        fetchCurrentUser();
    }, []);

    /* ─── Dados ─── */

    const fetchOpportunities = useCallback(async (isBackground = false) => {
        if (!isBackground) setIsLoading(true);
        try {
            const [opportunities, chatRes] = await Promise.all([
                OpportunityService.getAllOpportunities(),
                apiFetch(`${apiUrl}/api/Chat/ConversasPorVendedor`).then(r => r.json()),
            ]);

            const chatDataMap: Record<number, { tipoLeadId: number; campanha: boolean; idCampanha: number | null; nomeCampanha: string }> = {};
            if (chatRes.sucesso && Array.isArray(chatRes.dados)) {
                chatRes.dados.forEach((c: any) => {
                    chatDataMap[c.idConversa] = {
                        tipoLeadId: c.tipoLeadId, campanha: c.campanha, idCampanha: c.idCampanha, nomeCampanha: c.nomeCampanha || '',
                    };
                });
            }

            const newColumns = INITIAL_COLUMNS.map(col => ({ ...col, deals: [] as Deal[] }));
            const unmapped: Opportunity[] = [];

            opportunities.forEach(opp => {
                const statusId = opp.idStauts; // o nome do campo vem assim da API
                const createdDate = new Date(opp.dataConversaCriada);
                const extra = chatDataMap[opp.idConversa];
                const deal: Deal = {
                    id: opp.idConversa,
                    // Organizações, fase 2: sem contato, o negócio se chama pela organização.
                    title: opp.nomeContato || opp.nomeOrganizacao || opp.numeroWhatsapp,
                    value: brl(opp.valor || 0),
                    rawValue: opp.valor || 0,
                    tag: 'new',
                    owner: opp.nomeUsuarioResponsavel && opp.nomeUsuarioResponsavel !== 'IA' ? opp.nomeUsuarioResponsavel : 'Sem responsável',
                    date: dataCurta(createdDate),
                    rawDate: createdDate,
                    contactId: opp.idContato,
                    statusId,
                    tipoLeadId: opp.tipoLeadId !== undefined ? opp.tipoLeadId : extra?.tipoLeadId,
                    tipoContatoId: opp.tipoContatoId ?? null,
                    campanha: opp.campanha !== undefined ? opp.campanha : extra?.campanha,
                    idCampanha: opp.idCampanha !== undefined ? opp.idCampanha : extra?.idCampanha,
                    nomeCampanha: opp.nomeCampanha !== undefined ? opp.nomeCampanha : (extra?.nomeCampanha || ''),
                    phone: opp.numeroWhatsapp,
                    details: opp.detalhesConversa,
                    // A API devolve "IA" no nome quando não há responsável e, nas versões antigas,
                    // não manda o id. Sem id, o nome decide; -1 é "tem dono, id desconhecido".
                    ownerId: opp.idUsuarioResponsavel
                        ?? (opp.nomeUsuarioResponsavel && opp.nomeUsuarioResponsavel !== 'IA' ? -1 : undefined),
                    idOrigemOportunidade: opp.idOrigemOportunidade,
                    descricaoOrigemOportunidade: opp.descricaoOrigemOportunidade,
                    origemMarketing: opp.origemMarketing ?? null,
                    organizacaoId: opp.idOrganizacao ?? null,
                    organizacao: opp.nomeOrganizacao || undefined,
                    /* Sem mensagem nenhuma, "parado" conta da criação: um negócio criado à mão hoje
                       não está parado há 300 dias, e um de março sem nenhuma conversa está. */
                    diasParado: diasDesde(opp.dataUltimaInteracao ? new Date(opp.dataUltimaInteracao) : createdDate),
                };
                if (statusId !== 1) {
                    deal.tag = deal.tipoLeadId === 1 ? 'hot' : deal.tipoLeadId === 2 ? 'warm' : deal.tipoLeadId === 3 ? 'cold' : 'new';
                }
                const col = newColumns.find(c => c.statusId === statusId);
                if (col) col.deals.push(deal);
                else { unmapped.push(opp); (newColumns[2] ?? newColumns[0]).deals.push(deal); }
            });

            if (unmapped.length > 0) console.warn('Status sem coluna:', unmapped.map(d => d.idStauts));

            setColumns(newColumns);
            return newColumns;
        } catch (error) {
            console.error('Error fetching opportunities:', error);
            aviso.erro('Não foi possível carregar as oportunidades.');
            return [];
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    }, []);

    /* Uma ação do agente (painel ao lado da tela) pode mover, ganhar ou perder
       oportunidade: o quadro recarrega na hora, sem esperar os 15s. */
    useEffect(() => {
        const aoAgir = () => { if (!isDraggingRef.current) fetchOpportunities(true); };
        window.addEventListener(ACAO_ASSISTENTE_EVENT, aoAgir);
        return () => window.removeEventListener(ACAO_ASSISTENTE_EVENT, aoAgir);
    }, [fetchOpportunities]);

    useEffect(() => {
        fetchOpportunities();
        const interval = setInterval(() => {
            if (!isDraggingRef.current) fetchOpportunities(true);
        }, 15000);
        return () => clearInterval(interval);
    }, [fetchOpportunities]);

    const todos = useMemo(() => columns.flatMap(c => c.deals), [columns]);

    /* ─── Filtros ─── */

    const contar = (chave: (d: Deal) => string | null | undefined) => {
        const m = new Map<string, number>();
        todos.forEach(d => { const k = chave(d); if (k) m.set(k, (m.get(k) ?? 0) + 1); });
        return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([valor, total]) => ({ valor, total }));
    };

    const opcoesResponsavel = useMemo(() => contar(d => d.ownerId ? d.owner : null), [todos]); // sem dono não vira opção
    const opcoesOrganizacao = useMemo(() => contar(d => d.organizacao || null), [todos]);
    const opcoesCampanha = useMemo(() => contar(d => d.campanha ? (d.nomeCampanha || 'Sem nome') : null), [todos]);
    /* O filtro casa por ID (o nome é editável pela empresa) e mostra o nome atual. "Sem tipo"
       entra no fim, e só quando existe oportunidade sem tipo — senão é opção que não recorta nada. */
    const opcoesTipoContato = useMemo(() => {
        const comTipo = tiposContato
            .map(t => ({ valor: String(t.tipoContatoId), rotulo: t.nome, total: todos.filter(d => d.tipoContatoId === t.tipoContatoId).length }))
            .filter(o => o.total > 0);
        const sem = todos.filter(d => !d.tipoContatoId).length;
        return sem && comTipo.length ? [...comTipo, { valor: '', rotulo: 'Sem tipo', total: sem }] : comTipo;
    }, [todos, tiposContato]);
    /* As origens de lead que existem na pipeline. "Sem origem" entra no fim e só quando há alguma
       com origem — sozinho ele não filtra nada que o usuário não veja. */
    const opcoesOrigemLead = useMemo(() => {
        const comOrigem = contar(d => textoOrigemMarketing(d.origemMarketing));
        if (!comOrigem.length) return [];
        const sem = todos.filter(d => !textoOrigemMarketing(d.origemMarketing)).length;
        return sem ? [...comOrigem, { valor: SEM_ORIGEM, total: sem }] : comOrigem;
    }, [todos]);

    /* As colunas vêm do funil da empresa, carregado uma vez por sessão no MainLayout. Se ele
       chegar depois desta tela montar, este contador faz o quadro redesenhar — sem ele, quem
       abrisse a Pipeline primeiro veria as colunas padrão até trocar de tela. */
    const [funilVersao, setFunilVersao] = useState(0);
    useEffect(() => {
        const aoCarregar = () => setFunilVersao(v => v + 1);
        window.addEventListener(FUNIL_CARREGADO_EVENT, aoCarregar);
        return () => window.removeEventListener(FUNIL_CARREGADO_EVENT, aoCarregar);
    }, []);

    /* Só os canais que existem na pipeline, com o glifo da coluna — um chip que daria lista vazia não aparece. */
    const opcoesCanal = useMemo(() => {
        const vistos = new Map<string, ReactNode>();
        todos.forEach(d => { const o = origem(d.idOrigemOportunidade); if (!vistos.has(o.palavra)) vistos.set(o.palavra, o.glifo); });
        return [...vistos.entries()]
            .sort((a, b) => ORDEM_CANAIS.indexOf(a[0]) - ORDEM_CANAIS.indexOf(b[0]))
            .map(([valor, glifo]) => ({ valor, rotulo: <>{glifo} {valor}</> }));
    }, [todos]);
    const opcoesEtapa = useMemo(() => gruposDoFunil().map(g => ({ valor: g.title })), [funilVersao]);

    const filtrados = useMemo(() => {
        const q = busca.trim().toLowerCase();
        let inicio: Date | null = null; let fim: Date | null = null;
        if (periodo && periodo !== 'Personalizado') {
            inicio = new Date(); inicio.setDate(inicio.getDate() - Number(periodo.split(' ')[0])); inicio.setHours(0, 0, 0, 0);
        } else if (periodo === 'Personalizado') {
            if (dataDe) { inicio = new Date(dataDe + 'T00:00:00'); }
            if (dataAte) { fim = new Date(dataAte + 'T23:59:59.999'); }
        }
        const paradoHa = diasDoCorte(semInteracao);
        const lista = todos.filter(d => {
            if (q && !d.title.toLowerCase().includes(q) && !(d.phone ?? '').includes(q) && !(d.organizacao ?? '').toLowerCase().includes(q)) return false;
            // Dentro de um filtro os valores somam ("Ana ou Bruno"); entre filtros, restringem.
            if (responsavel.length && !responsavel.includes(d.owner)) return false;
            if (temperatura.length && (!d.tipoLeadId || !temperatura.includes(TEMPERATURA[d.tipoLeadId]?.rotulo))) return false;
            if (canal.length && !canal.includes(origem(d.idOrigemOportunidade).palavra)) return false;
            if (origemFiltro.length && !origemFiltro.includes(d.campanha ? 'Campanha' : 'Orgânica')) return false;
            if (origemLead.length && !origemLead.includes(textoOrigemMarketing(d.origemMarketing) ?? SEM_ORIGEM)) return false;
            if (organizacaoFiltro.length && !organizacaoFiltro.includes(d.organizacao ?? '')) return false;
            if (campanha.length && (!d.campanha || !campanha.includes(d.nomeCampanha || 'Sem nome'))) return false;
            if (inicio && d.rawDate < inicio) return false;
            if (fim && d.rawDate > fim) return false;
            if (etapa.length && !etapa.includes(grupoDoStatus(d.statusId)?.title ?? '')) return false;
            if (tipoContato.length && !tipoContato.includes(String(d.tipoContatoId ?? ''))) return false;
            if (paradoHa !== null && d.diasParado < paradoHa) return false;
            return true;
        });
        const { col, dir } = lerOrdem(ordem);
        const comparar = COMPARADORES[col] ?? COMPARADORES.criada;
        const vazio = VAZIO[col] ?? (() => false);
        // Vazio sempre no fim; empate desempata pela data, mais nova primeiro — a lista não "pula" entre renders.
        lista.sort((a, b) => {
            const va = vazio(a), vb = vazio(b);
            if (va !== vb) return va ? 1 : -1;
            return (dir === 'asc' ? comparar(a, b) : comparar(b, a)) || b.rawDate.getTime() - a.rawDate.getTime();
        });
        return lista;
    }, [todos, busca, responsavel, temperatura, origemFiltro, canal, origemLead, organizacaoFiltro, campanha, periodo, dataDe, dataAte, etapa, semInteracao, ordem]);

    /* Exporta o recorte da tela (quadro ou lista, é o mesmo), na ordem escolhida.
       Só o administrador — é a carteira inteira saindo do sistema. */
    const exportar = () => exportarListaCsv('oportunidades', filtrados, [
        { rotulo: 'Oportunidade', valor: d => d.title },
        { rotulo: 'Telefone', valor: d => d.phone, tipo: 'telefone' },
        { rotulo: 'Organização', valor: d => d.organizacao },
        { rotulo: 'Etapa', valor: d => nomeEtapa(d.statusId) },
        { rotulo: 'Valor', valor: d => d.rawValue ?? null, tipo: 'numero' },
        { rotulo: 'Responsável', valor: d => d.owner },
        { rotulo: 'Temperatura', valor: d => (d.tipoLeadId ? TEMPERATURA[d.tipoLeadId]?.rotulo : '') },
        { rotulo: 'Canal', valor: d => origem(d.idOrigemOportunidade).palavra },
        { rotulo: 'Campanha', valor: d => (d.campanha ? d.nomeCampanha || 'Sem nome' : '') },
        { rotulo: 'Criada em', valor: d => d.rawDate.toISOString(), tipo: 'data' },
    ]);

    // Mudar um filtro volta para a página 1 — mas não na montagem, senão a
    // página restaurada da sessão se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, responsavel, temperatura, origemFiltro, canal, origemLead, organizacaoFiltro, campanha, periodo, dataDe, dataAte, etapa, semInteracao, ordem, porPagina]);

    const dealsDoGrupo = (g: Grupo) => filtrados.filter(d => g.statusIds.includes(d.statusId));

    const limparFiltros = () => {
        setBusca(''); limparCampos();
    };

    /** O "Limpar todos" do painel: tudo que mora nele, sem apagar a busca, que mora na faixa. */
    const limparCampos = () => {
        setResponsavel([]); setTemperatura([]); setOrigemFiltro([]); setCanal([]); setOrigemLead([]); setOrganizacaoFiltro([]);
        setCampanha([]); setPeriodo(null); setDataDe(''); setDataAte(''); setEtapa([]); setSemInteracao(null);
        setTipoContato([]);
    };

    /* ─── Mover ─── */

    const moverLocal = (id: number, destino: number, patch: Partial<Deal> = {}) => {
        setColumns(prev => {
            let movido: Deal | undefined;
            const semEle = prev.map(c => {
                const d = c.deals.find(x => x.id === id);
                if (d) movido = d;
                return { ...c, deals: c.deals.filter(x => x.id !== id) };
            });
            if (!movido) return prev;
            const novo: Deal = { ...movido, ...patch, statusId: destino };
            /* Etapa criada pela empresa não tem coluna-modelo: o cartão vai para a mesma de reserva da
               carga (o quadro desenha pelo statusId do cartão). Sem isso ele sumia até recarregar. */
            const reserva = semEle.some(c => c.statusId === destino) ? destino : (semEle[2] ?? semEle[0]).statusId;
            return semEle.map(c => (c.statusId === reserva ? { ...c, deals: [novo, ...c.deals] } : c));
        });
    };

    const updateDealStatus = async (
        dealId: number, newStatusId: number, value?: number, motivoPerdaId?: number, motivoPerdaDetalhe?: string,
    ): Promise<{ success: boolean; message?: string }> => {
        try {
            const body: Record<string, unknown> = { idStatus: newStatusId, valor: value || 0 };
            if (motivoPerdaId) { body.motivoPerdaId = motivoPerdaId; body.motivoPerdaDetalhe = motivoPerdaDetalhe || null; }
            const response = await apiFetch(`${apiUrl}/api/Chat/Conversas/${dealId}/AtualizarStatus`, {
                method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
            });
            const result = await response.json();
            if (!response.ok || !result.sucesso) return { success: false, message: result.mensagem || 'Falha ao atualizar status.' };
            return { success: true };
        } catch (error: any) {
            console.error('Error updating status:', error);
            return { success: false, message: error.message || 'Erro ao atualizar status.' };
        }
    };

    const isProductRequiredError = (message?: string) => !!message?.toLowerCase().includes('produto');

    // Rolagem automática do quadro perto das bordas, enquanto se arrasta.
    const stopAutoScroll = () => {
        if (scrollAnimationFrameRef.current !== null) { cancelAnimationFrame(scrollAnimationFrameRef.current); scrollAnimationFrameRef.current = null; }
    };
    const startAutoScroll = (direction: 'left' | 'right', speed: number) => {
        const scroll = () => {
            if (boardRef.current) { boardRef.current.scrollLeft += direction === 'right' ? speed : -speed; scrollAnimationFrameRef.current = requestAnimationFrame(scroll); }
        };
        stopAutoScroll();
        scrollAnimationFrameRef.current = requestAnimationFrame(scroll);
    };
    const handleDragMove = (e: MouseEvent | TouchEvent) => {
        if (!isDraggingRef.current || !boardRef.current) { stopAutoScroll(); return; }
        const clientX = 'touches' in e && e.touches.length > 0 ? e.touches[0].clientX : (e as MouseEvent).clientX;
        const { left, right } = boardRef.current.getBoundingClientRect();
        const zona = 120; const maxSpeed = 12;
        if (clientX > right - zona) startAutoScroll('right', Math.max(2, ((clientX - (right - zona)) / zona) * maxSpeed));
        else if (clientX < left + zona) startAutoScroll('left', Math.max(2, (((left + zona) - clientX) / zona) * maxSpeed));
        else stopAutoScroll();
    };
    const onDragStart = () => {
        isDraggingRef.current = true;
        window.addEventListener('mousemove', handleDragMove, true);
        window.addEventListener('touchmove', handleDragMove, true);
    };

    const onDragEnd = async (result: DropResult) => {
        isDraggingRef.current = false;
        window.removeEventListener('mousemove', handleDragMove, true);
        window.removeEventListener('touchmove', handleDragMove, true);
        stopAutoScroll();

        const { destination, draggableId } = result;
        if (!destination) return;
        const destGrupo = gruposDoFunil().find(g => g.id === destination.droppableId);
        const deal = todos.find(d => d.id === Number(draggableId));
        if (!destGrupo || !deal) return;
        if (destGrupo.statusIds.includes(deal.statusId)) return; // mesma coluna: nada a gravar
        await moverParaEtapa(deal, destGrupo.alvo);
    };

    /**
     * Mover de etapa: o mesmo caminho para o arrastar e para o "Mudar etapa" do menu do botão
     * direito — uma ação, um nome, dois gestos. **Move na hora**, como a régua e o select da ficha;
     * quem interrompe é a regra da API: Ganho exige produto e Perdido pede o motivo. A temperatura
     * é opcional em qualquer etapa (menu do botão direito ou ficha).
     */
    const moverParaEtapa = async (deal: Deal, destino: number) => {
        if (deal.statusId === destino) return;
        const origemStatus = deal.statusId;

        if (destino === 3) {
            // Ganhar qualifica como quente; a API recusa sem produto vinculado.
            moverLocal(deal.id, 3, { tipoLeadId: 1, tag: 'hot' });
            try {
                if (deal.tipoLeadId !== 1) await ChatService.atualizarTipoLead(deal.id, 1);
                const r = await updateDealStatus(deal.id, 3, deal.rawValue);
                if (!r.success) {
                    if (isProductRequiredError(r.message)) { setPendingWonDeal({ ...deal, statusId: 3 }); setIsWinProductModalOpen(true); }
                    else { aviso.erro(r.message || 'Não foi possível atualizar o status.'); fetchOpportunities(true); }
                } else fetchOpportunities(true);
            } catch { fetchOpportunities(true); }
            return;
        }

        if (destino === 6) {
            moverLocal(deal.id, 6);
            setPendente({ deal, origem: origemStatus, destino });
            setIsLossReasonModalOpen(true);
            return;
        }

        moverLocal(deal.id, destino);
        const r = await updateDealStatus(deal.id, destino, deal.rawValue);
        if (!r.success) { aviso.erro(r.message || 'Não foi possível atualizar o status.'); fetchOpportunities(true); }
    };

    const handleLossReasonConfirm = async (motivoPerdaId: number, motivoPerdaDetalhe: string) => {
        if (!pendente) return;
        const { deal, destino } = pendente;
        const toastId = aviso.carregando('Registrando venda perdida…');
        const r = await updateDealStatus(deal.id, destino, deal.rawValue, motivoPerdaId, motivoPerdaDetalhe);
        if (r.success) aviso.ok('Venda marcada como perdida.', { id: toastId });
        else aviso.erro(r.message || 'Não foi possível registrar o motivo da perda.', { id: toastId });
        setIsLossReasonModalOpen(false);
        setPendente(null);
        fetchOpportunities(true);
    };

    const handleLossReasonCancel = () => {
        setIsLossReasonModalOpen(false);
        if (pendente) moverLocal(pendente.deal.id, pendente.origem);
        setPendente(null);
    };

    const handleWinProductConfirm = async () => {
        if (!pendingWonDeal) return;
        const toastId = aviso.carregando('Fechando venda…');
        const r = await updateDealStatus(pendingWonDeal.id, 3, pendingWonDeal.rawValue);
        if (r.success) aviso.ok('Venda fechada.', { id: toastId });
        else aviso.erro(r.message || 'Não foi possível fechar a venda.', { id: toastId });
        setIsWinProductModalOpen(false);
        setPendingWonDeal(null);
        fetchOpportunities(true);
    };

    const handleWinProductCancel = () => {
        setIsWinProductModalOpen(false);
        setPendingWonDeal(null);
        fetchOpportunities(true);
    };

    /* ─── Ações ─── */

    const assumir = async (deal: Deal) => {
        if (!currentUser || assumingDealId === deal.id) return;
        setAssumingDealId(deal.id);
        const toastId = aviso.carregando('Assumindo oportunidade…');
        try {
            const response = await apiFetch(`${apiUrl}/api/Chat/Conversas/${deal.id}/TranferirConversa`, {
                method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ IdResponsavelAtual: deal.ownerId && deal.ownerId > 0 ? deal.ownerId : 0, IdNovoResponsavel: currentUser.id }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData?.mensagem || 'Não foi possível assumir a oportunidade.');
            }
            aviso.ok('Oportunidade assumida.', { id: toastId });
            fetchOpportunities(true);
        } catch (err: any) {
            aviso.erro(textoDoErro(err, 'Não foi possível assumir a oportunidade.'), { id: toastId });
        } finally {
            setAssumingDealId(null);
            setTomando(null);
        }
    };

    /**
     * Quem pode assumir (2026-09-26): sem responsável, qualquer um — como
     * sempre foi. Com responsável, só o administrador da empresa, e nunca em
     * Ganho/Perdido: trocar o dono de venda fechada muda de quem é a venda nas
     * metas e no Analytics.
     */
    const podeAssumir = (deal: Deal) => {
        if (!currentUser) return false;
        if (!deal.ownerId) return true;
        return verTodos && deal.ownerId !== currentUser.id && deal.statusId !== 3 && deal.statusId !== 6;
    };

    /* Pegar a que está sem dono é um clique; tirar de outro vendedor pede confirmação. */
    const pedirAssumir = (deal: Deal) => {
        if (deal.ownerId) setTomando(deal);
        else assumir(deal);
    };

    const excluir = async (deal: Deal) => {
        setExcluindo(true);
        try {
            const response = await apiFetch(`${apiUrl}/api/Chat/Conversas/${deal.id}`, { method: 'DELETE' });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData?.mensagem || 'Não foi possível excluir a oportunidade.');
            }
            aviso.ok('Oportunidade excluída.');
            setColumns(prev => prev.map(c => ({ ...c, deals: c.deals.filter(d => d.id !== deal.id) })));
        } catch (err: any) {
            aviso.erro(textoDoErro(err, 'Não foi possível excluir a oportunidade.'));
        } finally {
            setExcluindo(false);
            setConfirmandoId(null);
        }
    };

    /* Alternativa a excluir: fornecedor ou contato pessoal não é negócio. O contato sai do funil
       (as conversas ficam no Chat) e a linha some do quadro, porque a API não a devolve mais. */
    const tirarDoFunil = async (deal: Deal) => {
        if (!deal.contactId) return;
        setExcluindo(true);
        try {
            await ContactService.definirFunil(deal.contactId, true);
            aviso.ok('Contato fora do funil. As conversas continuam no Chat.');
            setColumns(prev => prev.map(c => ({ ...c, deals: c.deals.filter(d => d.contactId !== deal.contactId || d.statusId === 3) })));
        } catch (err: any) {
            aviso.erro(textoDoErro(err, 'Não foi possível tirar o contato do funil.'));
        } finally {
            setExcluindo(false);
            setConfirmandoId(null);
        }
    };

    /* ─── Menu do botão direito ─── */

    /* O mesmo menu do cartão do quadro e da linha da Lista (negocioMenu.tsx). `toque` guarda o toque
       longo em andamento; `disparou` impede que o clique solto ao levantar o dedo abra a oportunidade
       por cima do menu — o mesmo cuidado da lista do Chat. */
    const [menu, setMenu] = useState<{ deal: Deal; x: number; y: number; porToque?: boolean } | null>(null);
    const toque = useRef<{ timer: number | null; disparou: boolean; quando: number }>({ timer: null, disparou: false, quando: 0 });
    const fecharMenu = useCallback(() => setMenu(null), []);
    const cancelarToque = useCallback(() => {
        if (toque.current.timer !== null) { window.clearTimeout(toque.current.timer); toque.current.timer = null; }
    }, []);
    useEffect(() => cancelarToque, [cancelarToque]);

    const abrirMenu = (e: MouseEventReact<HTMLElement>, deal: Deal) => {
        /* O menu nativo sai sempre: no Android o toque longo o dispara, e ele atropelaria o nosso. */
        e.preventDefault();
        e.stopPropagation();
        /* No celular quem abre é o temporizador do toque longo; o `contextmenu` que vem junto é o eco dele. */
        if (isMobile && Date.now() - toque.current.quando < 1000) return;
        /* Tecla de menu de contexto (ou Shift+F10): vem sem posição, então o menu nasce no cartão. */
        if (e.clientX === 0 && e.clientY === 0) {
            const caixa = e.currentTarget.getBoundingClientRect();
            setMenu({ deal, x: caixa.left + 24, y: caixa.bottom - 8 });
            return;
        }
        setMenu({ deal, x: e.clientX, y: e.clientY });
    };

    const comecarToque = (e: TouchEventReact<HTMLElement>, deal: Deal) => {
        toque.current.quando = Date.now();
        toque.current.disparou = false;
        if (!isMobile) return;
        const ponto = e.touches[0];
        cancelarToque();
        toque.current.timer = window.setTimeout(() => {
            toque.current.timer = null;
            toque.current.disparou = true;
            setMenu({ deal, x: ponto.clientX, y: ponto.clientY, porToque: true });
        }, TOQUE_LONGO_MS);
    };

    /** Abrir pelo toque curto, só quando o longo não disparou. */
    const tocarCartao = (deal: Deal) => {
        if (toque.current.disparou) { toque.current.disparou = false; return; }
        abrir(deal);
    };

    // Modais que o menu chama: os mesmos do Chat, nada novo desenhado aqui.
    const [resumoIA, setResumoIA] = useState('');
    const [resumoAberto, setResumoAberto] = useState(false);
    const [gerandoResumo, setGerandoResumo] = useState(false);
    const [transferindo, setTransferindo] = useState<Deal | null>(null);
    const [tarefaPara, setTarefaPara] = useState<Deal | null>(null);
    const [tiposTarefa, setTiposTarefa] = useState<TipoTarefa[]>([]);
    /* Exclusão pedida pelo menu no QUADRO (no cartão não há linha para confirmar dentro); na Lista o
       menu usa a mesma confirmação na linha da lixeira, que é o padrão de listagem. */
    const [excluindoDeal, setExcluindoDeal] = useState<Deal | null>(null);
    const [foraDoFunilDeal, setForaDoFunilDeal] = useState<Deal | null>(null);

    useEffect(() => {
        let vivo = true;
        TipoTarefaService.getAll()
            .then(t => { if (vivo) setTiposTarefa(t); })
            .catch(() => { /* sem tipos, o item "Criar tarefa" simplesmente não aparece */ });
        return () => { vivo = false; };
    }, []);

    const definirTemperatura = async (deal: Deal, tipoLeadId: number) => {
        const tag: Deal['tag'] = tipoLeadId === 1 ? 'hot' : tipoLeadId === 2 ? 'warm' : 'cold';
        setColumns(prev => prev.map(c => ({ ...c, deals: c.deals.map(d => (d.id === deal.id ? { ...d, tipoLeadId, tag } : d)) })));
        try {
            await ChatService.atualizarTipoLead(deal.id, tipoLeadId);
            aviso.ok('Temperatura do lead atualizada.');
        } catch (err) {
            aviso.erro(textoDoErro(err, 'Não foi possível atualizar a temperatura do lead.'));
            fetchOpportunities(true);
        }
    };

    /**
     * Passar o negócio para outro vendedor. A régua é a de assumir: o dono passa o seu adiante, quem
     * tem `conversas.todos` passa o de qualquer um, e **venda fechada não troca de dono** — isso
     * mudaria de quem é a venda nas metas e no Analytics.
     */
    const podeTransferir = (deal: Deal) => {
        if (!currentUser || !deal.ownerId) return false;
        if (deal.statusId === 3 || deal.statusId === 6) return false;
        return verTodos || deal.ownerId === currentUser.id;
    };

    const transferirPara = async (deal: Deal, usuario: InternalUser) => {
        const toastId = aviso.carregando('Transferindo oportunidade…');
        try {
            const response = await apiFetch(`${apiUrl}/api/Chat/Conversas/${deal.id}/TranferirConversa`, {
                method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ IdResponsavelAtual: deal.ownerId && deal.ownerId > 0 ? deal.ownerId : 0, IdNovoResponsavel: usuario.id }),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData?.mensagem || 'Não foi possível transferir a oportunidade.');
            }
            aviso.ok(`Oportunidade transferida para ${usuario.name}.`, { id: toastId });
            fetchOpportunities(true);
        } catch (err) {
            aviso.erro(textoDoErro(err, 'Não foi possível transferir a oportunidade.'), { id: toastId });
        }
    };

    const resumirConversa = async (deal: Deal) => {
        if (gerandoResumo) return;
        setGerandoResumo(true);
        const toastId = aviso.carregando('Gerando resumo da conversa com IA…');
        try {
            const resposta = await ChatService.getConversationSummary(deal.id);
            if (resposta.sucesso) {
                setResumoIA(resposta.dados);
                setResumoAberto(true);
                aviso.ok('Resumo gerado.', { id: toastId });
            } else {
                aviso.erro(resposta.mensagem || 'Não foi possível gerar o resumo.', { id: toastId });
            }
        } catch {
            aviso.erro('Não foi possível conectar com o serviço de IA.', { id: toastId });
        } finally {
            setGerandoResumo(false);
        }
    };

    const copiar = async (texto: string, ok: string, erro: string) => {
        try { await navigator.clipboard.writeText(texto); aviso.ok(ok); } catch { aviso.erro(erro); }
    };

    const enderecoDoNegocio = (deal: Deal) => `${window.location.origin}/pipeline/deal/${deal.id}`;

    const acoesDoNegocio = (deal: Deal): AcoesNegocio => ({
        moverEtapa: (_, destino) => { moverParaEtapa(deal, destino); },
        definirTemperatura: (_, tipoLeadId) => { definirTemperatura(deal, tipoLeadId); },
        assumir: () => pedirAssumir(deal),
        transferir: () => setTransferindo(deal),
        abrirConversa: () => navigate(`/chat?conversa=${deal.id}`),
        copiarTelefone: () => copiar((deal.phone ?? '').replace(/\D/g, ''), 'Telefone copiado.', 'Não foi possível copiar o telefone.'),
        resumir: () => { resumirConversa(deal); },
        criarTarefa: () => setTarefaPara(deal),
        // A ficha abre já na aba Anotações, com o formulário aberto — um passo, não três.
        adicionarAnotacao: () => navigate(`/pipeline/deal/${deal.id}?aba=notes&nova=1`),
        copiarLink: () => copiar(enderecoDoNegocio(deal), 'Link copiado.', 'Não foi possível copiar o link.'),
        abrirEmNovaAba: () => window.open(enderecoDoNegocio(deal), '_blank', 'noopener'),
        tirarDoFunil: () => setForaDoFunilDeal(deal),
        excluir: () => (view === 'lista' && !isMobile ? setConfirmandoId(deal.id) : setExcluindoDeal(deal)),
        permite: {
            assumir: podeAssumir(deal),
            transferir: podeTransferir(deal),
            criarTarefa: tiposTarefa.length > 0,
            excluir: podeExcluir,
        },
    });

    /* ─── Pedaços ─── */

    const Chip = ({ deal }: { deal: Deal }) => {
        if (deal.statusId === 1 || deal.statusId === 8 || !deal.tipoLeadId || !TEMPERATURA[deal.tipoLeadId]) return null;
        const t = TEMPERATURA[deal.tipoLeadId];
        return <span className={`pl-chip ${t.classe}`}><i aria-hidden="true" />{t.rotulo}</span>;
    };

    /** O conteúdo do cartão — o mesmo no quadro (arrastável) e no celular (lista). */
    const CorpoCartao = ({ deal }: { deal: Deal }) => {
        const o = origem(deal.idOrigemOportunidade);
        const ia = deal.statusId === 1 ? 'atendendo' : deal.statusId === 8 ? 'encerrou' : null;
        const temMid = (deal.rawValue ?? 0) > 0 || (!ia && !!deal.tipoLeadId && !!TEMPERATURA[deal.tipoLeadId]);
        return (
            <>
                        <div className={`pl-card-name ${pareceTelefone(deal.title) ? 'is-phone' : ''}`} title={deal.title}>{deal.title}</div>
                        {deal.organizacao && deal.organizacao !== deal.title && (
                            <div className="pl-card-org" title={deal.organizacao}>{deal.organizacao}</div>
                        )}
                        {temMid && (
                            <div className="pl-card-mid">
                                {(deal.rawValue ?? 0) > 0 && <span className="pl-card-val">{deal.value}</span>}
                                <Chip deal={deal} />
                            </div>
                        )}
                        <div className="pl-card-foot">
                            {o.glifo}
                            {deal.campanha && <LuMegaphone size={12} aria-label={deal.nomeCampanha ? `Campanha ${deal.nomeCampanha}` : 'Campanha'} />}
                            {ia
                                ? <><LuBot size={12} /><span className={`pl-w ${ia === 'encerrou' ? 'is-ia' : 'is-ia0'}`}>{ia === 'encerrou' ? 'IA encerrou' : 'IA atendendo'}</span></>
                                : <span className="pl-w">{o.palavra}</span>}
                            <span className="pl-sp" />
                            <span>{deal.date}</span>
                            {deal.ownerId ? <span className="pl-av" title={`Responsável: ${deal.owner}`}>{iniciais(deal.owner)}</span> : null}
                        </div>
                        {podeAssumir(deal) && (
                            <button type="button" className="pl-assume" disabled={assumingDealId === deal.id}
                                onClick={e => { e.stopPropagation(); pedirAssumir(deal); }}>
                                <LuUserPlus size={11} /> {assumingDealId === deal.id ? 'Assumindo…' : 'Assumir'}
                            </button>
                        )}
            </>
        );
    };

    const Cartao = ({ deal, index }: { deal: Deal; index: number }) => (
        <Draggable draggableId={String(deal.id)} index={index}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={`pl-card ${snapshot.isDragging ? 'is-dragging' : ''} ${menu?.deal.id === deal.id ? 'is-ctx' : ''}`}
                    style={provided.draggableProps.style}
                    aria-label={`Abrir ${deal.title}`}
                    onClick={() => tocarCartao(deal)}
                    onKeyDown={e => { if (e.key === 'Enter') abrir(deal); }}
                    onContextMenu={e => abrirMenu(e, deal)}
                    onTouchStart={e => comecarToque(e, deal)}
                    onTouchMove={cancelarToque}
                    onTouchEnd={cancelarToque}
                    onTouchCancel={cancelarToque}
                >
                    <CorpoCartao deal={deal} />
                </div>
            )}
        </Draggable>
    );

    /**
     * Celular (fase 3): a mesma leitura do quadro, uma etapa embaixo da outra,
     * com os mesmos cartões — sem arrastar. Substitui o acordeão legado; o que
     * ele embutia (chat, resumo) mora na página da oportunidade.
     */
    const ListaCelular = () => (
        <div className="pl-mobile">
            {gruposDoFunil().map(g => {
                const deals = dealsDoGrupo(g);
                const recolhida = g.recolhivel && !perdidoAberta;
                return (
                    <section key={g.id} className="pl-mgroup">
                        <div className="pl-col-head">
                            <i className={`pl-dot is-${g.cor}`} aria-hidden="true" />
                            <span className="pl-col-name">{g.title}</span>
                            <span className="pl-col-count">{deals.length}</span>
                            {g.recolhivel && (
                                <button type="button" className="pl-col-btn" aria-label={recolhida ? `Mostrar ${g.title}` : `Recolher ${g.title}`}
                                    onClick={() => setPerdidoAberta(v => !v)}>
                                    {recolhida ? <LuChevronsLeft size={14} style={{ transform: 'rotate(-90deg)' }} /> : <LuChevronsRight size={14} style={{ transform: 'rotate(-90deg)' }} />}
                                </button>
                            )}
                        </div>
                        {!recolhida && (
                            <div className="pl-mgroup-body">
                                {deals.length === 0 && <div className="pl-empty">Sem oportunidades</div>}
                                {deals.map(deal => (
                                    <div key={deal.id} className={`pl-card ${menu?.deal.id === deal.id ? 'is-ctx' : ''}`} role="button" tabIndex={0} aria-label={`Abrir ${deal.title}`}
                                        onClick={() => tocarCartao(deal)} onKeyDown={e => { if (e.key === 'Enter') abrir(deal); }}
                                        onContextMenu={e => abrirMenu(e, deal)}
                                        onTouchStart={e => comecarToque(e, deal)}
                                        onTouchMove={cancelarToque}
                                        onTouchEnd={cancelarToque}
                                        onTouchCancel={cancelarToque}>
                                        <CorpoCartao deal={deal} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                );
            })}
        </div>
    );

    const Coluna = ({ grupo }: { grupo: Grupo }) => {
        const deals = dealsDoGrupo(grupo);
        const soma = deals.reduce((acc, d) => acc + (d.rawValue || 0), 0);
        const recolhida = grupo.recolhivel && !perdidoAberta;

        if (recolhida) {
            return (
                <div className="pl-col is-rail">
                    <div className="pl-col-head">
                        <button type="button" className="pl-col-btn" aria-label={`Abrir a coluna ${grupo.title}`} onClick={() => setPerdidoAberta(true)}>
                            <LuChevronsLeft size={14} />
                        </button>
                    </div>
                    <div className="pl-col-sum" />
                    <Droppable droppableId={grupo.id}>
                        {(provided, snapshot) => (
                            <div ref={provided.innerRef} {...provided.droppableProps} className={`pl-rail-body ${snapshot.isDraggingOver ? 'is-over' : ''}`}>
                                <i className={`pl-dot is-${grupo.cor}`} aria-hidden="true" />
                                <button type="button" className="pl-rail-label" onClick={() => { setEtapa([grupo.title]); setView('lista'); }}
                                    aria-label={`Ver ${deals.length} oportunidades em ${grupo.title} na lista`}>
                                    {grupo.title} <span>{deals.length}</span>
                                </button>
                                <div style={{ display: 'none' }}>{provided.placeholder}</div>
                            </div>
                        )}
                    </Droppable>
                </div>
            );
        }

        return (
            <div className="pl-col">
                <div className="pl-col-head">
                    <i className={`pl-dot is-${grupo.cor}`} aria-hidden="true" />
                    <span className="pl-col-name">{grupo.title}</span>
                    <span className="pl-col-count">{deals.length}</span>
                    {grupo.plus && (
                        <button type="button" className="pl-col-btn" aria-label={`Nova oportunidade em ${grupo.title}`}
                            onClick={() => setPainelNovo({ aberto: true, etapa: grupo.alvo })}>
                            <LuPlus size={14} />
                        </button>
                    )}
                    {grupo.recolhivel && (
                        <button type="button" className="pl-col-btn" aria-label={`Recolher a coluna ${grupo.title}`} onClick={() => setPerdidoAberta(false)}>
                            <LuChevronsRight size={14} />
                        </button>
                    )}
                </div>
                <div className="pl-col-sum">{brl(soma)}</div>
                <Droppable droppableId={grupo.id}>
                    {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className={`pl-col-body ${snapshot.isDraggingOver ? 'is-over' : ''}`}>
                            {deals.map((deal, index) => <Cartao key={deal.id} deal={deal} index={index} />)}
                            {provided.placeholder}
                            {deals.length === 0 && !snapshot.isDraggingOver && <div className="pl-empty">Sem oportunidades</div>}
                        </div>
                    )}
                </Droppable>
            </div>
        );
    };

    /* ─── Lista ─── */

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const naPagina = filtrados.slice(inicio, inicio + porPagina);

    // Função, não componente: um componente declarado aqui nasceria novo a cada
    // render e o botão perderia o foco logo depois do clique.
    const cabecalho = (col: ColunaOrdem, rotulo: string) => {
        const ativo = ordemAtual.col === col;
        const Seta = !ativo ? LuChevronsUpDown : ordemAtual.dir === 'asc' ? LuChevronUp : LuChevronDown;
        const proxima = ativo ? (ordemAtual.dir === 'asc' ? 'decrescente' : 'crescente') : DIRECAO_PADRAO[col] === 'asc' ? 'crescente' : 'decrescente';
        return (
            <button type="button" className={`lk-head lk-head-sort ${ativo ? 'is-on' : ''}`} onClick={() => ordenarPor(col)}
                aria-sort={ativo ? (ordemAtual.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                title={`Ordenar por ${rotulo.toLowerCase()} (${proxima})`}>
                <span>{rotulo}</span><Seta size={12} aria-hidden="true" />
            </button>
        );
    };

    const Lista = () => (
        <div className="lk-table">
            <div className="lk-scroll">
                <div className="lk-row" style={{ gridTemplateColumns: colunasLista }}>
                    {cabecalho('titulo', 'Oportunidade')}
                    {cabecalho('organizacao', 'Organização')}
                    {cabecalho('etapa', 'Etapa')}
                    {cabecalho('valor', 'Valor')}
                    {cabecalho('temperatura', 'Temperatura')}
                    {cabecalho('responsavel', 'Responsável')}
                    {cabecalho('origem', 'Origem')}
                    {cabecalho('criada', 'Criada')}
                    <div className="lk-head lk-head-acts">Ações</div>
                </div>
                <div className="lk-body">
                    {naPagina.map(d => {
                        if (confirmandoId === d.id) {
                            /* So a exclusao: tirar do funil tem botao proprio na linha. Misturar os dois
                               na mesma pergunta fazia uma confirmacao decidir duas coisas diferentes. */
                            return <RowConfirm key={d.id}
                                question={`Excluir a oportunidade de "${d.title}"?`}
                                busy={excluindo}
                                onConfirm={() => excluir(d)} onCancel={() => setConfirmandoId(null)} />;
                        }
                        const g = grupoDoStatus(d.statusId);
                        const o = origem(d.idOrigemOportunidade);
                        const t = d.tipoLeadId ? TEMPERATURA[d.tipoLeadId] : undefined;
                        return (
                            <ListRow key={d.id} columns={colunasLista} label={d.title}
                                marcada={menu?.deal.id === d.id} onContextMenu={e => abrirMenu(e, d)}>
                                <span className="lk-cell lk-strong"><span title={d.title}>{d.title}</span></span>
                                <span className="lk-cell lk-muted">{d.organizacao && d.organizacao !== d.title ? <span title={d.organizacao}>{d.organizacao}</span> : <span className="lk-empty-value">—</span>}</span>
                                <span className="lk-cell"><span className="pl-etapa"><i className={`pl-dot is-${g?.cor ?? 'ia'}`} aria-hidden="true" /><span>{g?.title ?? '—'}</span></span></span>
                                <span className="lk-cell">{(d.rawValue ?? 0) > 0 ? <span className="pl-card-val">{d.value}</span> : <span className="lk-empty-value">—</span>}</span>
                                <span className="lk-cell">{t && d.statusId !== 1 && d.statusId !== 8 ? <span className={`pl-chip ${t.classe}`} style={{ marginLeft: 0 }}><i aria-hidden="true" />{t.rotulo}</span> : <span className="lk-empty-value">—</span>}</span>
                                <span className="lk-cell lk-muted">
                                    {d.ownerId && <span className="pl-owner" title={d.owner}><span className="pl-av" aria-hidden="true">{iniciais(d.owner)}</span><span>{d.owner}</span></span>}
                                    {podeAssumir(d) && <button type="button" className="pl-assume" disabled={assumingDealId === d.id} onClick={() => pedirAssumir(d)}><LuUserPlus size={11} /> Assumir</button>}
                                    {!d.ownerId && !podeAssumir(d) && <span className="lk-empty-value">—</span>}
                                </span>
                                <span className="lk-cell lk-muted" title={d.campanha && d.nomeCampanha ? `${o.palavra} · ${d.nomeCampanha}` : o.palavra}><span className="pl-etapa">{o.glifo}{d.campanha && <LuMegaphone size={12} />}</span></span>
                                <span className="lk-cell lk-muted"><span>{d.date}</span></span>
                                <RowActions>
                                    <RowAction icon={<LuEye size={14} />} label="Ver oportunidade" onClick={() => abrir(d)} />
                                    {/* Tirar do funil saiu de dentro do dialogo de excluir e virou botao proprio, a
                                        esquerda da lixeira: um e reversivel, o outro nao. So com contato e fora do
                                        Ganho — venda fechada nao sai do funil. Quando nao cabe, um vazio do tamanho
                                        do botao segura o lugar: sem ele o olho e a lixeira mudavam de coluna de uma
                                        linha para a outra, e a coluna inteira ficava tremida. */}
                                    {!!d.contactId && d.statusId !== 3
                                        ? <RowAction icon={<LuFilterX size={14} />} label="Tirar do funil" onClick={() => setForaDoFunilDeal(d)} />
                                        : <span aria-hidden="true" style={{ width: 26, flex: 'none' }} />}
                                    {podeExcluir && (
                                        <RowAction icon={<LuTrash2 size={14} />} label="Excluir oportunidade" danger onClick={() => setConfirmandoId(d.id)} />
                                    )}
                                </RowActions>
                            </ListRow>
                        );
                    })}
                </div>
            </div>

            {filtrados.length === 0 && (
                <div className="lk-blank">
                    <div className="lk-blank-title">{todos.length === 0 ? 'Nenhuma oportunidade ainda' : 'Nada com esses filtros'}</div>
                    <p className="lk-blank-text">
                        {todos.length === 0
                            ? 'As oportunidades aparecem aqui conforme chegam pelo WhatsApp, ou você cria uma manualmente.'
                            : 'Ajuste a busca ou limpe os filtros para ver as outras.'}
                    </p>
                    {todos.length === 0
                        ? <button type="button" className="lk-btn is-primary" onClick={() => setPainelNovo({ aberto: true })}><LuPlus size={14} /> Nova oportunidade</button>
                        : <button type="button" className="lk-btn" onClick={limparFiltros}>Limpar filtros</button>}
                </div>
            )}

            {filtrados.length > 0 && (
                <div className="lk-foot">
                    <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
                    <span className="lk-range">{inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}</span>
                    <span className="lk-pages">
                        <button type="button" className="lk-page" aria-label="Página anterior" disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                            <LuChevronLeft size={13} />
                        </button>
                        <span className="lk-page is-current">{paginaAtual}</span>
                        <span className="lk-muted">de {totalPaginas}</span>
                        <button type="button" className="lk-page" aria-label="Próxima página" disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                            <LuChevronRight size={13} />
                        </button>
                    </span>
                </div>
            )}
        </div>
    );

    /* ─── Render ─── */

    const plural = (n: number) => `${n} ${n === 1 ? 'oportunidade' : 'oportunidades'}`;
    const contagem = filtrados.length !== todos.length ? `${filtrados.length} de ${todos.length}` : plural(todos.length);

    // Etapa só tem campo na lista e no celular (no quadro as etapas são as colunas) —
    // mas se veio ligada da lista, o campo aparece para dar como desligar.
    const mostraEtapa = view === 'lista' || isMobile || etapa.length > 0;
    const fmtData = (iso: string) => iso ? iso.split('-').reverse().slice(0, 2).join('/') : '…';
    const rotuloPeriodo = !periodo ? null
        : periodo === 'Personalizado' ? (dataDe || dataAte ? `de ${fmtData(dataDe)} até ${fmtData(dataAte)}` : 'Entre datas')
        : `Últimos ${periodo}`;
    const resumo = [
        etapa.join(', '), responsavel.join(', '), organizacaoFiltro.join(', '),
        temperatura.join(', '), canal.join(', '), origemLead.join(', '),
        origemFiltro.map(o => (o === 'Campanha' ? 'De campanha' : 'Sem campanha')).join(', '), campanha.join(', '), rotuloPeriodo ?? '',
        semInteracao ? `Parados há ${semInteracao}` : '',
        tipoContato.map(id => tiposContato.find(t => String(t.tipoContatoId) === id)?.nome ?? 'Sem tipo').join(', '),
    ].filter(Boolean);
    const ligados = resumo.length;
    const ordemAtiva = ORDENS.some(o => o.value === ordem) ? ordem : 'criada:desc';
    const seletorOrdem = (block: boolean) => (
        <Select size="control" block={block} icon={<LuArrowUpDown size={14} />} value={ordemAtiva} options={ORDENS} onChange={setOrdem} ariaLabel="Ordem" />
    );

    return (
        <div className="page-shell pl-screen lk-screen">
            <PageHeader
                title="Oportunidades"
                subtitle="Cada oportunidade na sua etapa, do primeiro contato ao fechamento"
                actions={
                    <div className="lk-actions">
                        {!isMobile && (
                            <div className="pl-seg" role="group" aria-label="Visão">
                                <button type="button" className={view === 'quadro' ? 'is-active' : ''} onClick={() => setView('quadro')}><LuKanban size={14} /> Quadro</button>
                                <button type="button" className={view === 'lista' ? 'is-active' : ''} onClick={() => setView('lista')}><LuList size={14} /> Lista</button>
                            </div>
                        )}
                        {podeConfigurarFunil && (
                            <button type="button" className="lk-btn" onClick={() => navigate('/funil')} data-tour="pipeline-configurar-funil">
                                <LuSettings size={14} /> Configurar funil
                            </button>
                        )}
                        {/* Na lista vazia a porta é a do aviso, no meio da tela. O quadro
                            não tem aviso — lá o botão fica, ou a tela perderia a porta. */}
                        {verTodos && todos.length > 0 && (
                            <button type="button" className="lk-btn" onClick={exportar} disabled={isLoading}>
                                <LuDownload size={14} /> Exportar
                            </button>
                        )}
                        {!(!isLoading && !isMobile && view === 'lista' && todos.length === 0) && (
                            <button type="button" className="lk-btn is-primary" onClick={() => setPainelNovo({ aberto: true })}>
                                <LuPlus size={14} /> Nova oportunidade
                            </button>
                        )}
                    </div>
                }
            />

            <div className="page-body pl-fill lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar por nome ou telefone…" value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>
                    {/* Os filtros moram no painel (26/09): a faixa tem largura fixa e não pula ao filtrar.
                        Desde 05/10 o painel abre preso ao botão, e o botão alterna. */}
                    <FiltrosBotao ref={filtrosAncora} ligados={ligados} aberto={filtrosAbertos} onClick={() => setFiltrosAbertos(a => !a)} />
                    <FiltrosResumo partes={resumo} />
                    {/* Na lista as setas do cabeçalho ordenam; no celular a ordem vai para a folha. */}
                    {view === 'quadro' && !isMobile && seletorOrdem(false)}
                    <span className="lk-count pl-count">{contagem}</span>
                </div>

                <FiltrosPainel
                    open={filtrosAbertos}
                    onClose={() => setFiltrosAbertos(false)}
                    subtitulo={isMobile ? `${filtrados.length} de ${plural(todos.length)}` : `Aplicados na hora · ${filtrados.length} de ${plural(todos.length)}`}
                    ligados={ligados}
                    onLimpar={limparCampos}
                    celular={isMobile}
                    verRotulo={`Ver ${plural(filtrados.length)}`}
                    ancora={filtrosAncora}
                >
                    {/* Quem · o quê · quando. */}
                    <FiltroColuna>
                        {mostraEtapa && (
                            <FiltroCampo rotulo="Etapa" onLimpar={etapa.length ? () => setEtapa([]) : undefined}>
                                <FiltroChips opcoes={opcoesEtapa} valores={etapa} onChange={setEtapa} />
                            </FiltroCampo>
                        )}
                        <FiltroCampo rotulo="Responsável" onLimpar={responsavel.length ? () => setResponsavel([]) : undefined}>
                            <FiltroMulti opcoes={opcoesResponsavel} valores={responsavel} onChange={setResponsavel} />
                        </FiltroCampo>
                        <FiltroCampo rotulo="Organização" onLimpar={organizacaoFiltro.length ? () => setOrganizacaoFiltro([]) : undefined}>
                            <FiltroMulti opcoes={opcoesOrganizacao} valores={organizacaoFiltro} onChange={setOrganizacaoFiltro} vazio="Todas" />
                        </FiltroCampo>
                    </FiltroColuna>
                    <FiltroColuna>
                        <FiltroCampo rotulo="Temperatura" onLimpar={temperatura.length ? () => setTemperatura([]) : undefined}>
                            <FiltroChips opcoes={TEMPERATURAS} valores={temperatura} onChange={setTemperatura} />
                        </FiltroCampo>
                        {/* Tipo de contato ao lado da temperatura: as duas respondem "que gente é
                            esta". Só aparece com a funcionalidade ligada e a lista cadastrada. */}
                        {usaTipoContato && tiposContato.length > 0 && (
                            <FiltroCampo rotulo="Tipo de contato" onLimpar={tipoContato.length ? () => setTipoContato([]) : undefined}>
                                <FiltroMulti opcoes={opcoesTipoContato} valores={tipoContato} onChange={setTipoContato} vazio="Todos" />
                            </FiltroCampo>
                        )}
                        <FiltroCampo rotulo="Origem" onLimpar={canal.length ? () => setCanal([]) : undefined}>
                            <FiltroChips opcoes={opcoesCanal} valores={canal} onChange={setCanal} />
                        </FiltroCampo>
                        {/* "Origem" ali em cima é o CANAL (onde se conversa). Este é de ONDE a pessoa
                            veio — só aparece quando alguma oportunidade tem origem rastreada. */}
                        {opcoesOrigemLead.length > 0 && (
                            <FiltroCampo rotulo="Origem do lead" onLimpar={origemLead.length ? () => setOrigemLead([]) : undefined}>
                                <FiltroMulti opcoes={opcoesOrigemLead} valores={origemLead} onChange={setOrigemLead} vazio="Todas" />
                            </FiltroCampo>
                        )}

                        <FiltroCampo rotulo="Campanha" onLimpar={origemFiltro.length ? () => { setOrigemFiltro([]); setCampanha([]); } : undefined}>
                            <FiltroChips opcoes={ORIGENS} valores={origemFiltro} onChange={v => { setOrigemFiltro(v); if (!v.includes('Campanha')) setCampanha([]); }} />
                        </FiltroCampo>
                        {origemFiltro.includes('Campanha') && (
                            <FiltroCampo rotulo="Qual campanha" onLimpar={campanha.length ? () => setCampanha([]) : undefined}>
                                <FiltroMulti opcoes={opcoesCampanha} valores={campanha} onChange={setCampanha} vazio="Todas" />
                            </FiltroCampo>
                        )}
                    </FiltroColuna>
                    <FiltroColuna>
                        <FiltroCampo rotulo="Criada nos últimos" onLimpar={periodo ? () => { setPeriodo(null); setDataDe(''); setDataAte(''); } : undefined}>
                            <FiltroChips
                                unico
                                opcoes={PERIODOS.map(p => ({ valor: p, rotulo: p === 'Personalizado' ? <><LuCalendar size={12} /> Entre datas</> : p }))}
                                valores={periodo ? [periodo] : []}
                                onChange={v => { const p = v[0] ?? null; setPeriodo(p); if (p !== 'Personalizado') { setDataDe(''); setDataAte(''); } }}
                            />
                            {periodo === 'Personalizado' && (
                                <div className="lk-fdatas">
                                    <label><span>De</span><input type="date" value={dataDe} onChange={e => setDataDe(e.target.value)} /></label>
                                    <label><span>Até</span><input type="date" value={dataAte} onChange={e => setDataAte(e.target.value)} /></label>
                                </div>
                            )}
                        </FiltroCampo>
                        {/* O avesso do de cima: lá é quando o negócio ENTROU, aqui é há quanto tempo
                            ninguém fala com ele. Conta da última mensagem, em qualquer direção. */}
                        <FiltroCampo rotulo="Sem interação há mais de" onLimpar={semInteracao ? () => setSemInteracao(null) : undefined}>
                            <FiltroChips
                                unico
                                opcoes={PARADOS.map(p => ({ valor: p.valor }))}
                                valores={semInteracao ? [semInteracao] : []}
                                onChange={v => setSemInteracao(v[0] ?? null)}
                            />
                        </FiltroCampo>
                        {isMobile && (
                            <FiltroCampo rotulo="Ordem">{seletorOrdem(true)}</FiltroCampo>
                        )}
                    </FiltroColuna>
                </FiltrosPainel>

                {isLoading ? (
                    <PipelineSkeleton />
                ) : isMobile ? (
                    <ListaCelular />
                ) : view === 'lista' ? (
                    <Lista />
                ) : (
                    <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
                        <div className="pl-board" ref={boardRef}>
                            {gruposDoFunil().map(g => <Coluna key={g.id} grupo={g} />)}
                        </div>
                    </DragDropContext>
                )}
            </div>

            <CreateOpportunityPanel
                open={painelNovo.aberto}
                onClose={() => setPainelNovo({ aberto: false })}
                onCreated={() => fetchOpportunities(true)}
                statusInicial={painelNovo.etapa}
                usuarioAtual={currentUser}
            />

            <Dialog open={tomando !== null} onClose={() => setTomando(null)}
                title={`Assumir a oportunidade «${tomando?.title ?? ''}»?`}
                confirmLabel="Assumir" busy={tomando !== null && assumingDealId === tomando.id}
                onConfirm={() => { if (tomando) assumir(tomando); }}>
                Ela sai de {tomando?.owner} e passa a ser sua.
            </Dialog>
            <LossReasonModal isOpen={isLossReasonModalOpen} dealTitle={pendente?.deal.title ?? ''} onConfirm={handleLossReasonConfirm} onCancel={handleLossReasonCancel} />
            <WinDealProductModal isOpen={isWinProductModalOpen} dealId={pendingWonDeal?.id ?? 0} dealTitle={pendingWonDeal?.title ?? ''} onConfirm={handleWinProductConfirm} onCancel={handleWinProductCancel} />

            {/* O menu do botão direito. `herdarDe` é nulo de propósito: esta tela tem os tokens `--lk-*`,
                não os `--ch-*` do Chat, e o MenuContexto.css já traz o piso para esse caso. */}
            {menu && (
                <MenuContexto
                    itens={itensDoNegocio(menu.deal, acoesDoNegocio(menu.deal))}
                    posicao={{ x: menu.x, y: menu.y }}
                    titulo={menu.deal.title}
                    herdarDe={null}
                    aoFechar={fecharMenu}
                    abertoPorToque={menu.porToque}
                />
            )}

            <SummaryModal isOpen={resumoAberto} onClose={() => setResumoAberto(false)} summary={resumoIA} />

            {tarefaPara && (
                <CriarTarefaDialog
                    conversaId={tarefaPara.id}
                    nomeContato={tarefaPara.title}
                    tipos={tiposTarefa}
                    onFechar={() => setTarefaPara(null)}
                />
            )}

            {transferindo && (
                <TransferModal
                    onClose={() => setTransferindo(null)}
                    onTransfer={usuario => { const alvo = transferindo; setTransferindo(null); transferirPara(alvo, usuario); }}
                    currentUserId={currentUser?.id}
                />
            )}

            {/* Excluir pelo menu no quadro: no cartao nao ha linha para confirmar dentro, entao vale o
                dialogo. Tirar do funil tem item proprio no menu e dialogo proprio, logo abaixo. */}
            <Dialog open={excluindoDeal !== null} onClose={() => setExcluindoDeal(null)} danger busy={excluindo}
                title={`Excluir a oportunidade de «${excluindoDeal?.title ?? ''}»?`}
                confirmLabel="Excluir"
                onConfirm={() => { const alvo = excluindoDeal; setExcluindoDeal(null); if (alvo) excluir(alvo); }}>
                Ela sai do quadro e das contagens, e nao tem volta.
            </Dialog>

            <Dialog open={foraDoFunilDeal !== null} onClose={() => setForaDoFunilDeal(null)} busy={excluindo}
                title={`Tirar «${foraDoFunilDeal?.title ?? ''}» do funil?`}
                confirmLabel="Tirar do funil"
                onConfirm={() => { const alvo = foraDoFunilDeal; setForaDoFunilDeal(null); if (alvo) tirarDoFunil(alvo); }}>
                O contato sai do quadro e das listas de oportunidade. As conversas continuam no Chat.
            </Dialog>

            {/* O balão sobre "Configurar funil": só quem tem o botão (o Administrador). */}
            <TourNovidade tour={TOUR_CONFIGURAR_FUNIL} pronta={!isLoading && podeConfigurarFunil} />

        </div>
    );
};

export default PipelinePage;
